import {
    ArrayComparisonFilter,
    In,
    isArrayComparisonFilter,
    isComparisonFilter,
    QueryStringInput,
    stringifyQueryString
} from '@hello-aria/query-string'
import { isNil, get, isEmpty, uniq, chunk } from 'lodash'

import endpoints from '../../assets/__config__/endpoints.json'
import { RequestResponse } from '../services/requestServices'

export class HttpError extends Error {
    public readonly status: number
    public readonly code: string

    constructor(status: number, message?: string, code = 'HTTP_ERROR') {
        super(message)
        this.status = status
        this.code = code
        this.name = 'HttpError'
        Object.setPrototypeOf(this, HttpError.prototype)
    }
}

type JoinWithDots<K, P> = K extends string ? (P extends string ? `${K}${'' extends P ? '' : '.'}${P}` : never) : never
type KeepDeepestPaths<T> =
    T extends Record<string, unknown> ? { [K in keyof T]-?: JoinWithDots<K, KeepDeepestPaths<T[K]>> }[keyof T] : ''
export type Endpoints = KeepDeepestPaths<typeof endpoints>

export type ResolveApiUrlOptions = {
    variables?: {
        [name: string]: string | undefined
    }
    query?: QueryStringInput
}

/**
 * Replace all the strings between {{foo}} by the according options key.
 * @param endpoint   Endpoint to add after base URL.
 * @param options    Optional object that contains key/value pairs to replace endpoint dynamic properties.
 * @returns {string} The formatted url.
 */

const resolveBaseUrl = (key: Endpoints) => {
    const isServer = typeof window === 'undefined'
    const absoluteBaseUrl = isServer ? process.env.AUTH0_BASE_URL : ''

    switch (true) {
        case key.startsWith('pennylane'):
            return process.env.PENNYLANE_BASE_URL

        case key.startsWith('internal'):
            return `${absoluteBaseUrl}/api`

        case key.startsWith('aria'):
            return process.env.NEXT_PUBLIC_BASE_API_URL

        default:
            return '/api/proxy'
    }
}

export const resolveApiUrl = (key: Endpoints, options?: ResolveApiUrlOptions): string => {
    const { variables, query } = options || {}
    const endpoint = get(endpoints, key)
    let formattedUrl = `${resolveBaseUrl(key)}${endpoint}`

    if (!isNil(variables)) {
        Object.keys(variables).forEach((variableKey) => {
            const value = variables[variableKey]
            if (value && formattedUrl.includes(`{{${variableKey}}}`)) {
                formattedUrl = formattedUrl.replace(new RegExp(`{{${variableKey}}}`, 'g'), value)
            }
        })
    }

    const { sorts, pagination, filters, params } = query || {}
    const stringifiedParams = stringifyQueryString({ sorts, pagination, filters, params })
    const operator = isEmpty(stringifiedParams) || isNil(stringifiedParams) ? '' : '?'
    return formattedUrl + operator + stringifiedParams
}

type InFilterOptions = {
    filterProperty?: string
}

export function createInFilter<T extends readonly unknown[]>(
    items: T | undefined,
    propertyAccessor: (item: T[number]) => string | null | undefined,
    options: InFilterOptions = {}
): ArrayComparisonFilter[] {
    if (!items || isEmpty(items)) {
        return []
    }

    const values = uniq(items.map(propertyAccessor).filter((value): value is string => value != null))

    if (isEmpty(values)) {
        return []
    }

    const filterProperty = options.filterProperty || 'id'
    return [In(filterProperty, values)]
}

const EXPORT_BATCH_CONCURRENCY = 5
const BATCH_FETCH_RETRY_COUNT = 1
const DEFAULT_BATCH_SIZE = 100

const fetchWithRetry = async <T>(fetcher: () => Promise<T>, retries = BATCH_FETCH_RETRY_COUNT): Promise<T> => {
    try {
        return await fetcher()
    } catch (error) {
        const errorName: unknown = get(error, 'name')
        const isCanceled = errorName === 'AbortError' || errorName === 'CanceledError'
        if (isCanceled || retries === 0) {
            throw error
        }
        return fetchWithRetry(fetcher, retries - 1)
    }
}

/**
 * Creates a wrapper around a fetcher function that automatically batches large array filter values
 * into smaller chunks to avoid potential API limitations.
 *
 * @template TParams - The type of parameters accepted by the fetcher function, must include an optional query property
 * @template TItem - The type of items returned in the response body
 *
 * @param {function} fetcher - The original fetch function that retrieves data
 * @param {number} [batchSize=100] - Maximum number of items to include in each batch request
 *
 * @returns {function} A wrapped version of the fetcher that handles batching automatically
 *                     and combines results from multiple requests
 *
 * @example
 * // Create a batched version of your API fetcher
 * const batchedUserFetcher = batchFetch(fetchUsers, 50);
 *
 * // Use it like the original fetcher - batching happens automatically when needed
 * const result = await batchedUserFetcher({
 *   query: {
 *     filters: [{ field: 'id', operator: 'in', value: largeArrayOfIds }]
 *   }
 * });
 */
export function batchFetch<TParams extends { query?: QueryStringInput }, TItem>(
    fetcher: (p: TParams) => Promise<RequestResponse<TItem[]> | undefined>,
    defaultBatchSize = DEFAULT_BATCH_SIZE
) {
    return async (params: TParams): Promise<RequestResponse<TItem[]> | undefined> => {
        const { query } = params
        const filters = query?.filters ?? []

        const pagination = query?.pagination
        const explicitPageSize = pagination && ('first' in pagination ? pagination.first : pagination.last)
        const batchSize = explicitPageSize ? Math.min(explicitPageSize, defaultBatchSize) : defaultBatchSize

        const batchableIndex = filters.findIndex(
            (f) =>
                isComparisonFilter(f) &&
                isArrayComparisonFilter(f) &&
                Array.isArray(f.value) &&
                f.value.length > batchSize
        )

        if (batchableIndex === -1) {
            return fetcher(params)
        }

        const batchableFilter = filters[batchableIndex]
        if (isNil(batchableFilter) || !isArrayComparisonFilter(batchableFilter)) {
            return fetcher(params)
        }
        const chunks = chunk(batchableFilter.value, batchSize)
        const results: (RequestResponse<TItem[]> | undefined)[] = []
        const signal: unknown = get(params, 'signal')

        for (const chunkGroup of chunk(chunks, EXPORT_BATCH_CONCURRENCY)) {
            const groupResults = await Promise.all(
                chunkGroup.map((chunkValue) => {
                    const updatedFilters = [...filters]
                    updatedFilters[batchableIndex] = {
                        ...batchableFilter,
                        value: chunkValue
                    }

                    let chunkPagination = pagination
                    if (pagination) {
                        chunkPagination =
                            'last' in pagination
                                ? { ...pagination, last: chunkValue.length }
                                : { ...pagination, first: chunkValue.length }
                    }

                    const chunkedParams: TParams = {
                        ...params,
                        query: {
                            ...query,
                            pagination: chunkPagination,
                            filters: updatedFilters
                        }
                    }

                    return fetchWithRetry(() => {
                        if (signal instanceof AbortSignal) {
                            signal.throwIfAborted()
                        }
                        return fetcher(chunkedParams)
                    })
                })
            )
            results.push(...groupResults)
        }

        const valid = results.filter((r): r is RequestResponse<TItem[]> => !isNil(r))

        if (isEmpty(valid)) return undefined

        return {
            ...valid[0],
            body: valid.flatMap((r) => r.body ?? [])
        }
    }
}
